import { Card, Table, Tag, Spin } from "antd";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAppointments, AppointmentItem } from "../../../api/Appointment";
import { getAppointmentDetailPath } from "../../../utils/appointmentRoute";
import { useTranslation } from "react-i18next";

interface Props {
    customerId?: number;
}

export default function OrdersPanel({ customerId }: Props) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState<AppointmentItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });

    const fetchData = useCallback(async (page = 1) => {
        if (!customerId) return;
        setLoading(true);
        const res = await getAppointments({
            customer_id: customerId,
            page,
            limit: pagination.pageSize,
        });
        if (res.status && res.data) {
            setData(res.data.items);
            setPagination(prev => ({ ...prev, current: page, total: res.data.total }));
        }
        setLoading(false);
    }, [customerId, pagination.pageSize]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Trạng thái lịch hẹn (đồng bộ với trang /consultations)
    const statusMap: Record<number, { label: string; color: string }> = {
        1: { label: "Đặt lịch mới", color: "blue" },
        2: { label: "Đã xác nhận", color: "cyan" },
        3: { label: "Chờ thực hiện", color: "orange" },
        4: { label: "Đã thực hiện", color: "green" },
        0: { label: "Hủy lịch", color: "red" },
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "—";
        try {
            return new Date(dateStr).toLocaleString(i18n.language === 'vi' ? "vi-VN" : "en-US", {
                day: "2-digit", month: "2-digit", year: "numeric",
                hour: "2-digit", minute: "2-digit",
            });
        } catch { return dateStr; }
    };

    return (
        <Card
            title={
                <span className="text-gray-900 text-000000e0">
                    {t('cd.appointments')}
                </span>
            }
            // extra={
            //     <a href={`/order/create?customer_id=${customerId}`} className="cursor-pointer text-[#3e91ab] hover:underline">
            //         {t('cd.create_appointment')}
            //     </a>
            // }
            className="dark:bg-[#141414] dark:border-[#303030]"
        >
            <Table
                className="custom-ant-table"
                loading={loading}
                locale={{ emptyText: t('cd.no_appointment') }}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onChange: (page) => fetchData(page),
                }}
                columns={[
                    {
                        title: t('cd.col_code'),
                        dataIndex: "code",
                        // Lịch hẹn có thể đang nằm ở màn hình khác tùy thẻ trạng thái
                        // (customer_tag_page: 1 = lịch hẹn, 2 = lịch tư vấn, 3 = lịch thực hiện).
                        // Thiếu/không hợp lệ -> giữ ở màn hình lịch hẹn.
                        render: (code: string, record: AppointmentItem) => {
                            const path = getAppointmentDetailPath(record.customer_tag_page, record.id)
                                || `/appointment/detail/${record.id}`;
                            return (
                                <span
                                    onClick={() => navigate(path)}
                                    className="text-[#3e91ab] font-medium cursor-pointer hover:underline"
                                >
                                    {code}
                                </span>
                            );
                        },
                    },
                    {
                        title: t('cd.col_service'),
                        dataIndex: "services",
                        width: 220,
                        render: (_: any, record: AppointmentItem) => {
                            const services = record.services;
                            if (!services || services.length === 0) return "—";
                            return (
                                <div style={{ maxWidth: 200 }}>
                                    {services.map((s) => (
                                        <div
                                            key={s.id}
                                            style={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                            title={s.name}
                                        >
                                            {s.name}
                                        </div>
                                    ))}
                                </div>
                            );
                        },
                    },
                    {
                        title: t('cd.col_branch'),
                        dataIndex: "branch",
                        render: (branch: any) => branch?.name || "—",
                    },
                    {
                        title: t('cd.col_scheduled_at'),
                        dataIndex: "scheduled_at",
                        render: (val: string) => formatDate(val),
                    },
                    {
                        title: t('cd.col_created_at'),
                        dataIndex: "created_at",
                        render: (val: string) => formatDate(val),
                    },
                    {
                        title: t('cd.col_status'),
                        // Ưu tiên trạng thái thẻ khách (customer_tag_status) như trang /consultations
                        render: (_: any, record: AppointmentItem) => {
                            const statusNum = (record as any).customer_tag_status ?? record.status;
                            const statusText = (record as any).customer_tag_status_text;
                            const s = statusMap[statusNum];
                            if (statusText) {
                                return <Tag color={s?.color || "default"}>{statusText}</Tag>;
                            }
                            return s ? <Tag color={s.color}>{s.label}</Tag> : <Tag>{statusNum}</Tag>;
                        },
                    },
                ]}
                dataSource={data}
                rowKey="id"
                scroll={{ x: "max-content" }}
            />
        </Card>
    );
}